import { Form, Modal, Tag, message } from 'antd';
import { useState } from 'react';
import userApi from '../../apis/user';
import { ButtonType } from '../../constants/style';
import { IUser } from '../../interfaces/UserModel';
import Button from '../Button/Button';
import CustomForm from '../Form/Form';
import CustomInput from '../Form/FormItem/Input/Input';
import BaseModal from './BaseModal/BaseModal';

export interface ApiAccessPasswordModalProps {
  visible: boolean;
  user?: IUser;
  onCancel?: () => void;
  /** Called after the password was set or removed so the caller can refresh. */
  onChanged?: () => void;
}

// ValidationPipe errors arrive as an array of messages.
const errorText = (err: any, fallback: string) =>
  Array.isArray(err?.message)
    ? err.message.join(', ')
    : err?.message || fallback;

// Mirrors the API rule (8–72 printable ASCII, no spaces) in plain words.
const PASSWORD_PATTERN = /^[\x21-\x7E]+$/;

/**
 * Admin-only: issues, resets or removes the second password a seller must
 * enter on the "Truy cập API" page before managing API tokens.
 */
const ApiAccessPasswordModal = (props: ApiAccessPasswordModalProps) => {
  const { visible, user, onCancel, onChanged } = props;

  const [form] = Form.useForm<{ password: string; confirm: string }>();
  const [saving, setSaving] = useState(false);
  const isSet = !!user?.hasApiAccessPassword;

  const close = () => {
    form.resetFields();
    onCancel?.();
  };

  const setPassword = async () => {
    if (!user?.id) {
      return;
    }
    setSaving(true);
    try {
      await userApi.setApiAccessPassword(
        user.id,
        form.getFieldValue('password')
      );
      message.success('Đã đặt mật khẩu truy cập API');
      onChanged?.();
      close();
    } catch (err: any) {
      console.error(err);
      message.error(errorText(err, 'Không đặt được mật khẩu truy cập API'));
    } finally {
      setSaving(false);
    }
  };

  const removePassword = () => {
    if (!user?.id) {
      return;
    }
    const userId = user.id;
    Modal.confirm({
      title: 'Gỡ mật khẩu truy cập API?',
      content: `${user.username} sẽ không quản lý được API token cho đến khi được đặt mật khẩu mới. Các token stk_ hiện có vẫn hoạt động bình thường.`,
      okText: 'Gỡ mật khẩu',
      okButtonProps: { danger: true },
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await userApi.clearApiAccessPassword(userId);
          message.success('Đã gỡ mật khẩu truy cập API');
          onChanged?.();
          close();
        } catch (err: any) {
          console.error(err);
          message.error(errorText(err, 'Không gỡ được mật khẩu truy cập API'));
        }
      },
    });
  };

  return (
    <BaseModal
      visibleModal={visible}
      title="Mật khẩu truy cập API"
      onSecondaryBtnClick={close}
      onPrimaryBtnClick={() => form.submit()}
      primaryBtn={isSet ? 'Đặt lại mật khẩu' : 'Đặt mật khẩu'}
      secondaryBtn="Hủy"
      confirmLoading={saving}
    >
      <h4>Seller: {user?.username}</h4>
      <p>
        Trạng thái:{' '}
        {isSet ? <Tag color="green">đã đặt</Tag> : <Tag>chưa đặt</Tag>}
      </p>
      <p>
        Seller phải nhập mật khẩu này trên trang &quot;Truy cập API&quot; trước
        khi xem hoặc tạo API token. Chỉ chia sẻ cho người được phép quản lý
        token.
      </p>
      <p style={{ marginBottom: 16 }}>
        Mật khẩu dài 8–72 ký tự, gồm chữ không dấu, số và các ký hiệu thường
        gặp trên bàn phím (ví dụ <code>! @ # - _</code>). Không dùng khoảng
        trắng hoặc chữ có dấu.
      </p>
      <CustomForm
        form={form}
        layout="vertical"
        scrollToFirstError
        requiredMark="optional"
        onFinish={setPassword}
      >
        <Form.Item
          name="password"
          label="Mật khẩu truy cập API mới"
          style={{ marginBottom: 16 }}
          rules={[
            { required: true, message: 'Vui lòng nhập mật khẩu truy cập API' },
            { min: 8, message: 'Mật khẩu phải có ít nhất 8 ký tự' },
            { max: 72, message: 'Mật khẩu tối đa 72 ký tự' },
            {
              pattern: PASSWORD_PATTERN,
              message:
                'Chỉ dùng chữ không dấu, số và ký hiệu; không dùng khoảng trắng',
            },
          ]}
        >
          <CustomInput
            placeholder="Nhập mật khẩu"
            type="password"
            autoComplete="new-password"
          />
        </Form.Item>
        <Form.Item
          name="confirm"
          label="Nhập lại mật khẩu"
          dependencies={['password']}
          hasFeedback
          style={{ marginBottom: isSet ? 16 : 0 }}
          rules={[
            { required: true, message: 'Vui lòng nhập lại mật khẩu' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error('Mật khẩu nhập lại không khớp')
                );
              },
            }),
          ]}
        >
          <CustomInput
            placeholder="Nhập lại mật khẩu"
            type="password"
            autoComplete="new-password"
          />
        </Form.Item>
      </CustomForm>
      {isSet && (
        <Button type={ButtonType.DANGER} onClick={removePassword}>
          Gỡ mật khẩu truy cập API
        </Button>
      )}
    </BaseModal>
  );
};

export default ApiAccessPasswordModal;
